import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { RevealLines, FadeUp } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { Footer } from "../components/Footer";
import { useLang, pick, formatPrice } from "../lib/i18n";
import { http, fetchProducts, useBag, slugFromWorld } from "../lib/api";
import { THEMES } from "./Collection";

const Gallery = ({ images, name }) => (
  <div className="flex flex-col gap-4">
    {images.map((src, i) => (
      <motion.div
        key={src + i}
        initial={{ clipPath: "inset(8% 8% 8% 8%)", opacity: 0.4 }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1.3, ease: [0.19, 1, 0.22, 1] }}
        className="overflow-hidden"
      >
        <img data-testid={`product-gallery-image-${i}`} src={src} alt={name} className="w-full h-auto object-cover" />
      </motion.div>
    ))}
  </div>
);

const Buy = ({ p, street }) => {
  const { t, lang } = useLang();
  const { add } = useBag();
  const [size, setSize] = useState(null);
  const [warn, setWarn] = useState(false);
  const submit = () => {
    if (!size) { setWarn(true); return; }
    add({ id: p.id, size }, `${pick(p, "name", lang)} — ${size} · ${t("added")}`);
  };
  return (
    <div className="mt-10">
      <p className="f-mono text-[11px] uppercase tracking-[0.3em] mb-4 opacity-80">{t("size")}</p>
      <div className="flex flex-wrap gap-2">
        {(p.sizes || []).map((s) => (
          <button
            key={s}
            data-testid={`size-option-${s}`}
            onClick={() => { setSize(s); setWarn(false); }}
            className={`min-w-[3.25rem] h-12 px-3 f-mono text-[13px] border transition-colors duration-300 ${size === s ? (street ? "bg-[var(--acid)] text-black border-[var(--acid)]" : "bg-[var(--navy)] text-[var(--ivory)] border-[var(--navy)]") : (street ? "border-white/25 hover:border-white" : "border-[var(--navy)]/25 hover:border-[var(--navy)]")}`}
          >
            {s}
          </button>
        ))}
      </div>
      {warn && <p data-testid="size-warning" className="mt-3 text-sm text-red-500">{t("pickSize")}</p>}
      <button
        data-testid="add-to-cart-button"
        onClick={submit}
        className={`mt-8 w-full h-14 rounded-full f-mono text-[12px] uppercase tracking-[0.25em] transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0 ${street ? "bg-[var(--acid)] text-black" : "bg-[var(--navy)] text-[var(--ivory)]"}`}
      >
        {t("addToBag")} — {formatPrice(p.price)}
      </button>
    </div>
  );
};

export default function Product() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [p, setP] = useState(undefined);
  const [related, setRelated] = useState([]);
  useEffect(() => {
    setP(undefined);
    http.get(`/products/${id}`).then((r) => setP(r.data ?? null)).catch(() => setP(null));
  }, [id]);
  useEffect(() => {
    if (!p?.world) return;
    fetchProducts(p.world).then((all) => setRelated(all.filter((x) => x.id !== p.id).slice(0, 3)));
  }, [p]);

  if (p === undefined) return <main className="min-h-screen bg-[var(--ink)]" />;
  if (p === null) {
    return (
      <main data-testid="product-not-found" className="min-h-screen bg-[var(--ink)] text-white flex items-center justify-center flex-col gap-6">
        <p className="f-street uppercase text-6xl">{t("notFound")}</p>
        <Link to="/" className="f-mono text-xs uppercase tracking-[0.3em] underline">{t("back")}</Link>
      </main>
    );
  }
  const street = p.world === "street";
  const th = THEMES[p.world];
  const name = pick(p, "name", lang);
  const images = p.images?.length ? p.images : ["/images/hero_street.jpg"];
  return (
    <main data-testid="product-page" className={th.page}>
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 px-5 md:px-10 pt-32 md:pt-36 pb-28">
        <div className="lg:col-span-7"><Gallery images={images} name={name} /></div>
        <div className="lg:col-span-4 lg:col-start-9">
          <div className="lg:sticky lg:top-32">
            <Link to={`/collections/${slugFromWorld(p.world)}`} data-testid="product-back-link" className="inline-flex items-center gap-2 f-mono text-[11px] uppercase tracking-[0.3em] opacity-80 hover:opacity-100">
              <ArrowLeft size={13} /> {t(p.world)}
            </Link>
            {p.tag && <p className={`mt-8 f-mono text-[11px] uppercase tracking-[0.3em] ${th.kicker}`}>{p.tag}</p>}
            <RevealLines testid="product-name" inView={false} lines={[name]} className={`mt-3 ${street ? "f-street uppercase text-5xl md:text-7xl leading-[0.85]" : "f-serif italic text-5xl md:text-6xl leading-[1]"}`} />
            <p data-testid="product-price" className="mt-6 f-mono text-lg">{formatPrice(p.price)}</p>
            <FadeUp className={`mt-8 text-base leading-relaxed ${street ? "text-zinc-300" : "text-[var(--navy)]/80"}`}>
              {pick(p, "desc", lang)}
            </FadeUp>
            <Buy p={p} street={street} />
          </div>
        </div>
      </section>
      {related.length > 0 && (
        <section className="px-5 md:px-10 pb-28">
          <p className={`f-mono text-[11px] uppercase tracking-[0.3em] mb-10 ${th.kicker}`}>{t("related")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((r, i) => <ProductCard key={r.id} p={r} index={i} total={related.length} />)}
          </div>
        </section>
      )}
      <Footer />
    </main>
  );
}
